import { z } from "zod";
import { AgentIdSchema } from "./primitives.js";
import { ConversationKindFilterSchema, SessionListDataSchema } from "./sessions.js";

/**
 * Workspace RPC (SPA-side wire validation). `GET /api/workspace/conversations`
 * returns the caller's conversations merged across the agents they are granted,
 * recency-ordered and keyset-paged, each row carrying its agent's identity.
 *
 * The gateway does NOT import this module — it is the SPA's response validator.
 * The per-conversation shape is derived from the existing sessions contract so
 * the two stay in lockstep without editing it.
 */

const WorkspaceConversationSchema = SessionListDataSchema.shape.conversations.element.extend({
  agentId: AgentIdSchema,
  agentName: z.string(),
  agentIconUrl: z.string().optional(),
});
export type WorkspaceConversationView = z.infer<typeof WorkspaceConversationSchema>;

export const WorkspaceConversationsPayloadSchema = z
  .object({
    /** Opaque keyset cursor of the last row of the previous page. */
    before: z.string().min(1).optional(),
    limit: z.number().int().positive().max(100).optional(),
    /** Omitted means `chat`: scheduled runs never arrive unasked. */
    kind: ConversationKindFilterSchema.optional(),
  })
  .strict();
export type WorkspaceConversationsPayload = z.infer<typeof WorkspaceConversationsPayloadSchema>;

export const WorkspaceConversationsDataSchema = z.object({
  conversations: z.array(WorkspaceConversationSchema),
  hasMore: z.boolean(),
  nextCursor: z.string().optional(),
  /** Opaque fingerprint of the caller-scoped candidate set before paging. */
  revision: z.string().min(1),
  /** Matching conditional reads omit page rows and carry this marker. */
  unchanged: z.literal(true).optional(),
});
export type WorkspaceConversationsData = z.infer<typeof WorkspaceConversationsDataSchema>;
